import { afterNextRender, ChangeDetectorRef, Directive, effect, ElementRef, HostBinding, HostListener, input, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { UmbracoImage } from 'src/core-lib/umbraco/umbraco-image.model';
import { DomUtils } from 'src/utils/dom.util';
import { ContentCacheUtils } from 'src/utils/storage.util';

export type ImageSize = 'content-full' | 'content-half' | 'content-third' | 'content-quart';
export type ImageFit = 'cover' | 'contain' | 'height' | 'width';

type ImageData = {
    use: UmbracoImage | string,
    alt: string,
    role: string,
    width: number,
    height: number,
    aspect: string,
    srcset: string,
    size: ImageSize,
    fit: ImageFit,
    loading: string,
    query: { [key: string]: string },
}

@Directive({
    selector: 'img[use]'
})
export class ImgDirective {

    static QUERY_ENTRIES = {
        'bgcolor': 'transparent',
        'rsampler': 'mitchell',
        'format': 'webp',
        'rmode': 'pad',
    };
    static SIZES = [64, 128, 256, 320, 375, 425, 512, 768, 896, 1024, 1280, 1440, 1920, 2560];
    private static MinSize = {
        'content-full': 1920,
        'content-half': 1920 / 2,
        'content-third': 1920 / 3,
        'content-quart': 1920 / 4,

        'product-checkout': 256,
        'product-card': 512,
        'product-full': 1024,
        'product-half': 512
    };
    static QUERIES = {
        'content-full': '(min-width:1920px) 1920px, 100vw',
        'content-half': '(min-width:1790px) 960px, (min-width: 1180px) 768px, (min-width: 1024px) 512px, 100vw',
        'content-third': '(min-width:1790px) 768px, (min-width: 1760px) 512px, (min-width: 1475px) 425px, (min-width: 1295px) 375px, (min-width: 1024px) 320px, 100vw',
        'content-quart': '(min-width:1920px) 512px, (min-width: 1800px) 425px, (min-width: 1520px) 375px, (min-width: 1210px) 320px, (min-width: 1024px) 256px, 100vw',

        'accessory': '100px',
        'product-checkout': '256px',
        'product-card': '(min-width:1500px) 25vw, (min-width: 1200px) 33vw, 50vw',
        'product-full': '(min-width:1060px) 50vw, 100vw',
        'product-half': '(min-width:1060px) 25vw, 50vw',
    }

    private _retries: number;

    @HostBinding('attr.alt')
    private _alt?: string;
    @HostBinding('attr.role')
    private _role?: string;
    @HostBinding('attr.src')
    private _src?: string;
    @HostBinding('attr.loading')
    private _loading: string = 'lazy';
    @HostBinding('attr.srcset')
    private _srcset: string;
    @HostBinding('attr.sizes')
    private _size: string;
    @HostBinding('attr.style')
    private _style: string;

    use = input<UmbracoImage | string>();
    alt = input<string>();
    role = input<string>();
    width = input<number>();
    height = input<number>();
    size = input<ImageSize>();
    aspect = input<string>();
    fit = input<ImageFit>();
    loading = input<string>();
    srcset = input<string>();
    query = input<{ [key: string]: string }>();

    constructor(
        private _elementRef: ElementRef<HTMLImageElement>,
        private _detector: ChangeDetectorRef,
    ) {
        effect(() => {
            let use = this.use(),
                alt = this.alt(),
                role = this.role(),
                width = this.width(),
                height = this.height(),
                aspect = this.aspect(),
                size = this.size(),
                fit = this.fit(),
                loading = this.loading(),
                srcset = this.srcset(),
                query = this.query();

            let data: ImageData = { use, alt, role, width, height, aspect, fit, loading, query, size, srcset };
            if (ImgDirective._isInvalid(data))
                return;
            this._retries = 0;
            const imageElm = (this._elementRef.nativeElement as HTMLImageElement);
            DomUtils.clearChildren(imageElm);
            var _url = ImgDirective._getURL(this, data);
            this._src = _url.href;
            this._role = ImgDirective._getRole(this, data);
            this._size = ImgDirective._getSize(this, data);
            this._srcset = ImgDirective._sourceSet(_url, data);
            this._style = ImgDirective._getStyle(this, data);
            this._alt = ImgDirective._getAlt(this, data);
            this._loading = ImgDirective._getLoading(this, data);
            this._detector.markForCheck();
        })
    }

    @HostListener('error')
    onError() {
        if (this._retries > 5) {
            this._alt='';
            this._src='';
            this._role='presentation'
            return;
        }
        let use = this.use(),
            alt = this.alt(),
            role = this.role(),
            width = this.width(),
            height = this.height(),
            aspect = this.aspect(),
            fit = this.fit(),
            size = this.size(),
            loading = this.loading(),
            srcset = this.srcset(),
            query = this.query();
        let data: ImageData = { use, alt, role, width, height, aspect, fit, loading, query, size, srcset };
        this._alt = ImgDirective._getAlt(this, data);
        this._src = ImgDirective._fallbackUrl().href;
        this._srcset = '';
    }

    private static _getRole(_this: ImgDirective, data: ImageData): string {
        return data.role ?? (data.alt == null || data.alt == '' ? 'presentation' : null);
    }

    private static _getLoading(_this: ImgDirective, data: ImageData): string {
        return data.loading ?? 'lazy';
    }

    private static _getStyle(_this: ImgDirective, data: ImageData): string {
        if (data.fit == 'contain')
            return 'width:unset; height:unset; object-fit:contain;';
        if (data.fit == 'cover')
            return 'width:unset; height:unset; object-fit:cover;';
        if (data.fit == 'height') {
            let max = +((data?.use as UmbracoImage)?.aspectRatio?.split(':')?.findLast(() => true) ?? this.MinSize[data?.size]);
            return `width:auto; height:max(100%,${max}px); max-height: 100%; aspect-ratio:${((data?.use as UmbracoImage)?.aspectRatio ?? '1')?.replace(':', '/')};`;
        }
        if (data.fit == 'width') {
            let max = +((data?.use as UmbracoImage)?.aspectRatio?.split(':')?.find(() => true) ?? this.MinSize[data?.size]);
            return `width:max(${max}px,100%); max-width: 100%; height:auto; aspect-ratio:${((data?.use as UmbracoImage)?.aspectRatio ?? '1')?.replace(':', '/')};`;
        }
    }

    private static _getSize(_this: ImgDirective, data: ImageData): string {
        return this.QUERIES[data?.size] ?? '100vw';
    }



    private static _getURL(_this: ImgDirective, _data: ImageData): URL {
        let url = ImgDirective._url(_data.use);
        if (url.pathname.endsWith('.svg'))
            return url;
        let mappedQuery = Object.assign({}, this.QUERY_ENTRIES, _data.query);
        Object.entries(mappedQuery).forEach(([key, value]) => url.searchParams.set(key, value));
        return url;
    }

    static _fallbackUrl(): URL {
        var system = ContentCacheUtils.system<{ theme: { fallbackUrl: { imageUrl: string } } }>();
        return new URL(system?.theme?.fallbackUrl?.imageUrl ?? '/favicon.svg', window.origin)
    }

    static _sourceSet(_url: URL, data?: ImageData, sizes = this.SIZES): string {
        if(data?.srcset != null && typeof data?.srcset == 'string' && (data?.srcset??'').trim()?.length > 0)
            return data?.srcset;
        let _aspect = (data?.aspect ?? '1').split('/').reduce((acc, curr) => acc / +curr, 1)
        if (_url.pathname.endsWith('.svg'))
            return '';
        return sizes.map(size => {
            _url.searchParams.set('width', size + '');
            if (data?.aspect == null)
                _url.searchParams.delete('height');
            else {
                _url.searchParams.set('height', '' + Math.round(size * _aspect));
            }
            return `${_url.href} ${size}w`
        }).join(', ');
    }

    private static _getAlt(_this: ImgDirective, _data: ImageData) {
        return _this._alt != null && _this._alt.length > 0 ? _this._alt : typeof _this.use == 'string' ? _this._alt : ((_data.use as UmbracoImage)?.alt ?? _this._alt);
    }

    private static _isInvalid(_data: ImageData): boolean {
        return _data.use == null
    }

    private static _url(use: UmbracoImage | string | null): URL {
        if (use == null)
            return this._fallbackUrl();
        if (typeof use == 'string')
            return new URL(use);
        let _url = new URL(use.imageUrl, globalThis.location.origin);
        if (use.focalPoint != null)
            _url.searchParams.set('rxy', `${use.focalPoint.y},${use.focalPoint.x}`);
        return _url;
    }
}
